import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import {
  ColorOverrides,
  ElementAddress,
  ImageBrushData,
  MediaImage,
  PrintTechnology,
} from 'editor/src/store/design/types';
import getNewColorOverrides from 'editor/src/store/design/util/getNewColorOverrides';
import getImageById from 'editor/src/store/gallery/selector/getImageById';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';
import shouldDigitizeCurrentSpread from 'editor/src/store/utils/shouldDigitizeCurrentSpread';

import { extractColors } from 'editor/src/component/TopMenuDesktop/buttons/ButtonImageColor/utils';

import getAssetUrlWithReplacedColors from './getAssetUrlWithReplacedColors';
import updateMediaElementOperation, { MediaUpdateActionName } from './updateMediaElementOperation';

// used to recolor SVGs and quantized images
const updateImageElementColorsOperation =
  (elementAddress: ElementAddress, color: ColorOverrides) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    const element = getMediaElement<MediaImage>(state, elementAddress);
    const spread = state.design.designData?.spreads[elementAddress.spreadIndex];
    const variantKey = state.variants.selectedGroupKey;
    const designOptionKey = state.variants.selectedDesignOptionValue;

    if (!spread || !element) {
      return;
    }

    const newFillColors = getNewColorOverrides(element.colorOverrides?.fill || [], color);
    const newStrokeColors = getNewColorOverrides(element.colorOverrides?.stroke || [], color);

    const hasOverrides = newFillColors.length > 0 || newStrokeColors.length > 0;

    // Calculate thread colors for embroidery using the same logic as Thread Colors panel
    let threadColors: string[] | undefined;
    if (hasOverrides && spread.technology === PrintTechnology.Embroidery) {
      const digitizeDesignElements = shouldDigitizeCurrentSpread(state);
      const image = getImageById(state, element.imageId);
      const imageColors = digitizeDesignElements ? image?.quantizedColors : image?.colors;

      if (imageColors) {
        // Same logic as useCurrentElementColors
        const elementColors = extractColors(imageColors);
        const overrides: { [color: string]: string } = {};

        // Apply new overrides (newFillColors/newStrokeColors already include existing ones)
        newFillColors.forEach((c) => {
          overrides[c.previous] = c.new;
        });
        newStrokeColors.forEach((c) => {
          overrides[c.previous] = c.new;
        });

        // Get final thread colors by applying overrides
        threadColors = elementColors.map((colorData) => overrides[colorData.previous] || colorData.new);
      }
    }

    const colorOverrides: ImageBrushData | undefined = hasOverrides
      ? {
          fill: newFillColors,
          stroke: newStrokeColors,
          threadColors,
          url: undefined,
          urlTimestamp: undefined,
        }
      : undefined;

    dispatch(updateMediaElementOperation(elementAddress, { colorOverrides }, MediaUpdateActionName.COLOR_CHANGED));
    if (colorOverrides) {
      getAssetUrlWithReplacedColors(
        element.imageId,
        element.uuid,
        colorOverrides,
        spread.technology === PrintTechnology.Embroidery,
        variantKey,
        designOptionKey,
      );
    }
  };

export default updateImageElementColorsOperation;
